import React, { useReducer, useEffect, useCallback, useMemo, useState } from 'react';
import 'katex/dist/katex.min.css';
import { useLocation, useNavigate } from 'react-router-dom';
import FlashcardSession from './FlashcardSession';
import QuizSession from './QuizSession';
import AICoachChat from './AICoachChat';
import CourseView from './CourseView';
import AgendaView from './AgendaView';
import WeeklyCadreView from './WeeklyCadreView';
import GradesView from './GradesView';
import SubscriptionView from './SubscriptionView';
import ConversationHistory from './ConversationHistory';
import SkillsView from './SkillsView';
import ClasseInvites from '../ClasseInvites';
import { apiClient } from '../../utils/apiClient';
import { SubjectIcon } from '../SubjectIcons';
import { generateSlug } from '../../utils/slugUtils';

// ─── Reducer ──────────────────────────────────────────────────────────────────

const initialState = {
    // Données API
    cardsCount: null,
    quizzesCount: null,
    subjectCounts: { cards: {}, quizzes: {} },
    stats: null,
    chapters: [],
    // UI tableau de bord
    selectedSubject: 'Toutes',
    isSubjectMenuOpen: false,
    showAllChapters: false,
    isStartingCards: false,
    isStartingQuizzes: false,
    // Sessions actives
    isSessionActive: false,
    isQuizSessionActive: false,
    activeCards: null,
    activeQuizzes: null,
    sessionReport: null,
};

function reducer(state, action) {
    switch (action.type) {
        case 'FETCH_CARDS_SUCCESS':
            return { ...state, cardsCount: action.count, subjectCounts: { ...state.subjectCounts, cards: action.subjectCounts } };
        case 'FETCH_CARDS_ERROR':
            return { ...state, cardsCount: 0 };
        case 'FETCH_QUIZZES_SUCCESS':
            return { ...state, quizzesCount: action.count, subjectCounts: { ...state.subjectCounts, quizzes: action.subjectCounts } };
        case 'FETCH_QUIZZES_ERROR':
            return { ...state, quizzesCount: 0 };
        case 'FETCH_STATS_SUCCESS':
            return { ...state, stats: action.data };
        case 'FETCH_CHAPTERS_SUCCESS':
            return { ...state, chapters: action.chapters };
        case 'SELECT_SUBJECT':
            return { ...state, selectedSubject: action.subject, isSubjectMenuOpen: false };
        case 'TOGGLE_SUBJECT_MENU':
            return { ...state, isSubjectMenuOpen: !state.isSubjectMenuOpen };
        case 'CLOSE_SUBJECT_MENU':
            return { ...state, isSubjectMenuOpen: false };
        case 'TOGGLE_SHOW_ALL_CHAPTERS':
            return { ...state, showAllChapters: !state.showAllChapters };
        case 'START_LOADING_CARDS':
            return { ...state, isStartingCards: true };
        case 'STOP_LOADING_CARDS':
            return { ...state, isStartingCards: false };
        case 'START_CARDS_SESSION':
            return { ...state, isSessionActive: true, activeCards: action.cards, isStartingCards: false };
        case 'EXIT_CARDS_SESSION':
            return { ...state, isSessionActive: false, activeCards: null, sessionReport: action.report ?? null };
        case 'START_LOADING_QUIZZES':
            return { ...state, isStartingQuizzes: true };
        case 'STOP_LOADING_QUIZZES':
            return { ...state, isStartingQuizzes: false };
        case 'START_QUIZ_SESSION':
            return { ...state, isQuizSessionActive: true, activeQuizzes: action.quizzes, isStartingQuizzes: false };
        case 'EXIT_QUIZ_SESSION':
            return { ...state, isQuizSessionActive: false, activeQuizzes: null, sessionReport: action.report ?? null };
        default:
            return state;
    }
}

// ─── Composant ────────────────────────────────────────────────────────────────

function EtudiantView({ user, upcomingEvents = [], futureEvents = [], timeOffset = 0 }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [state, dispatch] = useReducer(reducer, initialState);
    const [, setCurrentChapter] = useState(null);
    const [planningTabActions, setPlanningTabActions] = useState(null);
    const {
        cardsCount, quizzesCount, subjectCounts, stats, chapters,
        selectedSubject, showAllChapters, isStartingCards, isStartingQuizzes,
        isSessionActive, isQuizSessionActive, activeCards, activeQuizzes, sessionReport,
    } = state;

    const isCourseViewActive   = location.pathname.startsWith('/apprendre');
    const isAgendaViewActive   = location.pathname.startsWith('/agenda');
    const isCadreViewActive    = location.pathname.startsWith('/planning');
    const isGradesViewActive   = location.pathname.startsWith('/notes');
    const isSubViewActive      = location.pathname.startsWith('/abonnement');
    const isHistoryViewActive  = location.pathname.startsWith('/historique');
    const isSkillsViewActive   = location.pathname.startsWith('/exercice');

    // ─── Fetch helpers ────────────────────────────────────────────────────────

    const fetchCardsCount = useCallback(async () => {
        try {
            const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_flashcard_session.php?action=count&id=${encodeURIComponent(user.id)}&t=${Date.now()}`, { cache: 'no-store' });
            const data = await res.json();
            if (data.success) dispatch({ type: 'FETCH_CARDS_SUCCESS', count: data.count, subjectCounts: data.subject_counts || {} });
            else dispatch({ type: 'FETCH_CARDS_ERROR' });
        } catch (err) { console.error("Erreur de chargement:", err); dispatch({ type: 'FETCH_CARDS_ERROR' }); }
    }, [user.id]);

    const fetchQuizzesCount = useCallback(async () => {
        try {
            const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_quiz_session.php?action=count&id=${encodeURIComponent(user.id)}&t=${Date.now()}`, { cache: 'no-store' });
            const data = await res.json();
            if (data.success) dispatch({ type: 'FETCH_QUIZZES_SUCCESS', count: data.count, subjectCounts: data.subject_counts || {} });
            else dispatch({ type: 'FETCH_QUIZZES_ERROR' });
        } catch (err) { console.error("Erreur de chargement Quiz:", err); dispatch({ type: 'FETCH_QUIZZES_ERROR' }); }
    }, [user.id]);

    const fetchStats = useCallback(async () => {
        try {
            const subjectParam = selectedSubject !== 'Toutes' ? `&subject=${encodeURIComponent(selectedSubject)}` : '';
            const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_student_stats.php?id=${encodeURIComponent(user.id)}${subjectParam}&t=${Date.now()}`, { cache: 'no-store' });
            const data = JSON.parse(await res.text());
            if (data.success) dispatch({ type: 'FETCH_STATS_SUCCESS', data });
        } catch (err) { console.error("Erreur de chargement stats:", err); }
    }, [user.id, selectedSubject]);

    const fetchChapters = useCallback(async () => {
        try {
            const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_course.php`);
            const data = await res.json();
            if (data.success) dispatch({ type: 'FETCH_CHAPTERS_SUCCESS', chapters: data.chapters });
        } catch (err) { console.error("Erreur chargement chapitres:", err); }
    }, []);

    useEffect(() => {
        fetchCardsCount();
        fetchQuizzesCount();
        fetchStats();
        fetchChapters();
    }, [fetchCardsCount, fetchQuizzesCount, fetchStats, fetchChapters]);

    // ─── Données dérivées ─────────────────────────────────────────────────────

    const activeSubjectsSet = new Set(['Toutes']);
    if (stats?.available_subjects) stats.available_subjects.forEach(s => activeSubjectsSet.add(s));
    Object.keys(subjectCounts.cards).forEach(s => activeSubjectsSet.add(s));
    Object.keys(subjectCounts.quizzes).forEach(s => activeSubjectsSet.add(s));
    upcomingEvents.forEach(e => activeSubjectsSet.add(e.subject_name || 'Général'));
    futureEvents.forEach(e => activeSubjectsSet.add(e.subject_name || 'Général'));
    const activeSubjects = Array.from(activeSubjectsSet);

    const currentCardsCount   = selectedSubject === 'Toutes' ? cardsCount   : (subjectCounts.cards[selectedSubject]   || 0);
    const currentQuizzesCount = selectedSubject === 'Toutes' ? quizzesCount : (subjectCounts.quizzes[selectedSubject] || 0);

    const subjectsWithRevisions = Array.from(new Set([
        ...Object.keys(subjectCounts.cards).filter(s => subjectCounts.cards[s] > 0),
        ...Object.keys(subjectCounts.quizzes).filter(s => subjectCounts.quizzes[s] > 0),
    ]));

    const isFullyDone  = cardsCount === 0 && quizzesCount === 0;
    const isSubjectDone = selectedSubject !== 'Toutes' && currentCardsCount === 0 && currentQuizzesCount === 0;
    const showCheckmark = isFullyDone || isSubjectDone;

    const displayedChapters = selectedSubject === 'Toutes' ? chapters : chapters.filter(c => c.subject_name === selectedSubject);
    const visibleChapters   = showAllChapters ? displayedChapters : displayedChapters.slice(0, 3);
    const filteredEvents        = selectedSubject === 'Toutes' ? upcomingEvents : upcomingEvents.filter(e => (e.subject_name || 'Général') === selectedSubject);
    const filteredFutureEvents  = selectedSubject === 'Toutes' ? futureEvents  : futureEvents.filter(e => (e.subject_name || 'Général') === selectedSubject);
    const nextEvent = filteredFutureEvents[0] ?? null;

    const now = new Date(Date.now() + timeOffset);
    const areAllEventsCompleted = filteredEvents.length === 0 || filteredEvents.every(e => now >= e.instance_end);

    // ─── Contexte page pour Axio ──────────────────────────────────────────────

    const pageContext = useMemo(() => {
        const p = location.pathname;

        if (isSessionActive)      return `L'élève est en session de révision de flashcards.`;
        if (isQuizSessionActive)  return `L'élève est en session de quiz d'évaluation.`;

        if (p.startsWith('/apprendre')) {
            const parts = p.split('/').filter(Boolean);
            if (parts.length >= 3) {
                const chapter = chapters.find(ch =>
                    (ch.slug || generateSlug(ch.name)) === parts[2] && (ch.subject_slug || generateSlug(ch.subject_name || '')) === parts[1]
                );
                if (chapter) {
                    const m = chapter.mastery || {};
                    return `L'élève consulte le cours "${chapter.name}" (${chapter.subject_name}). Maîtrise : ${m.overall ?? 0}% (Notions ${m.notion ?? 0}%, Exercices ${m.exercice ?? 0}%).`;
                }
            }
            return `L'élève consulte la liste des cours.`;
        }
        if (p.startsWith('/agenda'))   return `L'élève consulte son agenda (séances planifiées avec tuteur/prof).`;
        if (p.startsWith('/planning')) return `L'élève consulte son emploi du temps hebdomadaire fixe (cadre de révisions personnelles).`;
        if (p.startsWith('/exercice')) return `L'élève consulte ses exercices de compétences.`;
        if (p.startsWith('/notes'))    return `L'élève consulte ses notes et résultats scolaires.`;

        // Accueil
        return `L'élève est sur la page d'accueil (tableau de bord).${selectedSubject !== 'Toutes' ? ` Matière filtrée : ${selectedSubject}.` : ''}`;
    }, [location.pathname, isSessionActive, isQuizSessionActive, chapters, selectedSubject]);

    // ─── Gestion des sessions ─────────────────────────────────────────────────

    const startSession = async (fetchedCards = null) => {
        if (fetchedCards?.length > 0) {
            dispatch({ type: 'START_CARDS_SESSION', cards: fetchedCards });
        } else if (currentCardsCount > 0) {
            dispatch({ type: 'START_LOADING_CARDS' });
            try {
                const subjectParam = selectedSubject !== 'Toutes' ? `&subject=${encodeURIComponent(selectedSubject)}` : '';
                const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_flashcard_session.php?id=${encodeURIComponent(user.id)}${subjectParam}&t=${Date.now()}`, { cache: 'no-store' });
                const data = await res.json();
                if (data.success && data.cards?.length > 0) {
                    dispatch({ type: 'START_CARDS_SESSION', cards: data.cards });
                } else {
                    dispatch({ type: 'STOP_LOADING_CARDS' });
                    await fetchCardsCount();
                }
            } catch (error) {
                console.error("Erreur lors de la récupération des flashcards:", error);
                dispatch({ type: 'STOP_LOADING_CARDS' });
            }
        }
    };

    const handleExitFlashcardSession = async (reportData) => {
        const report = reportData && !reportData.nativeEvent ? { type: 'Flashcards', data: reportData, t: Date.now() } : null;
        dispatch({ type: 'EXIT_CARDS_SESSION', report });
        await fetchCardsCount();
        await fetchStats();
    };

    const startQuizSession = async (fetchedQuizzes = null) => {
        if (fetchedQuizzes?.length > 0) {
            dispatch({ type: 'START_QUIZ_SESSION', quizzes: fetchedQuizzes });
        } else if (currentQuizzesCount > 0) {
            dispatch({ type: 'START_LOADING_QUIZZES' });
            try {
                const subjectParam = selectedSubject !== 'Toutes' ? `&subject=${encodeURIComponent(selectedSubject)}` : '';
                const res  = await apiClient(`${process.env.REACT_APP_API_BASE_URL}/get_quiz_session.php?id=${encodeURIComponent(user.id)}${subjectParam}&t=${Date.now()}`, { cache: 'no-store' });
                const data = await res.json();
                if (data.success && data.quizzes?.length > 0) {
                    dispatch({ type: 'START_QUIZ_SESSION', quizzes: data.quizzes });
                } else {
                    dispatch({ type: 'STOP_LOADING_QUIZZES' });
                    await fetchQuizzesCount();
                }
            } catch (error) {
                console.error("Erreur lors de la récupération des Quiz:", error);
                dispatch({ type: 'STOP_LOADING_QUIZZES' });
            }
        }
    };

    const handleExitQuizSession = async (reportData) => {
        const report = reportData && !reportData.nativeEvent ? { type: 'Quiz', data: reportData, t: Date.now() } : null;
        dispatch({ type: 'EXIT_QUIZ_SESSION', report });
        await fetchQuizzesCount();
        await fetchStats();
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            {isSessionActive && activeCards?.length > 0 ? (
                <FlashcardSession cards={activeCards} user={user} onExit={handleExitFlashcardSession} />
            ) : isQuizSessionActive && activeQuizzes?.length > 0 ? (
                <QuizSession quizzes={activeQuizzes} user={user} onExit={handleExitQuizSession} />
            ) : (isCourseViewActive || isSkillsViewActive) ? (
                isCourseViewActive ? (
                    <CourseView user={user} onExit={() => navigate('/')} defaultSubject={selectedSubject} onChapterChange={setCurrentChapter} />
                ) : (
                    <SkillsView />
                )
            ) : (isAgendaViewActive || isCadreViewActive) ? (
                <div className="space-y-0">
                    {/* Tab bar Séances / Planning + actions Planning */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex gap-1 bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl border border-gray-200 dark:border-white/[0.05] rounded-2xl p-1 w-fit">
                            <button
                                onClick={() => navigate('/agenda')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${isAgendaViewActive
                                    ? 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30 shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.05]'}`}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                Séances
                            </button>
                            <button
                                onClick={() => navigate('/planning')}
                                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${isCadreViewActive
                                    ? 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30 shadow-sm'
                                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.05]'}`}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>
                                Planning
                            </button>
                        </div>
                        {isCadreViewActive && planningTabActions}
                    </div>
                    {isAgendaViewActive ? (
                        <AgendaView user={user} onExit={() => navigate('/')} />
                    ) : (
                        <WeeklyCadreView user={user} onExit={() => navigate('/')} onRegisterTabActions={setPlanningTabActions} />
                    )}
                </div>
            ) : isGradesViewActive ? (
                <GradesView user={user} onExit={() => navigate('/')} />
            ) : isSubViewActive ? (
                <SubscriptionView user={user} onExit={() => navigate('/')} />
            ) : isHistoryViewActive ? (
                <ConversationHistory onExit={() => navigate('/')} />
            ) : (
                <div className="space-y-12">

                {/* --- INVITATIONS DE CLASSE --- */}
                <ClasseInvites />

                {/* --- FILTRE MATIÈRES --- */}
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" style={{ scrollbarWidth: 'none' }}>
                    {activeSubjects.map(subject => {
                        const isSelected = selectedSubject === subject;
                        return (
                            <button
                                key={subject}
                                onClick={() => dispatch({ type: 'SELECT_SUBJECT', subject })}
                                className={`shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                                    isSelected
                                        ? 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border-cyan-500/30 shadow-sm'
                                        : 'bg-white/60 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-600 dark:text-gray-400 hover:border-blue-300 dark:hover:border-blue-700 hover:text-gray-800 dark:hover:text-gray-200'
                                }`}
                            >
                                <SubjectIcon subject={subject} className="w-3.5 h-3.5" />
                                {subject === 'Toutes' ? 'Toutes' : subject}
                            </button>
                        );
                    })}
                </div>

                {/* --- MÉMORISATION --- */}
                <section>
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-6 flex items-center pt-4">
                        <span className={`p-2 rounded-xl mr-3 shadow-lg flex items-center justify-center transition-colors duration-500 ${showCheckmark ? 'bg-emerald-500 border border-emerald-500 text-white' : 'bg-white/[0.05] border border-gray-200 dark:border-white/[0.05] text-fuchsia-600 dark:text-fuchsia-400'}`}>
                            {showCheckmark ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/><path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/></svg>
                            )}
                        </span>
                        <span className="text-gray-800 dark:text-white">Mémorisation</span>
                    </h2>

                    {!isFullyDone && selectedSubject === 'Toutes' ? (
                        <div className="bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-cyan-500/30 shadow-[0_0_20px_rgba(6,182,212,0.1)] p-8 text-center flex flex-col items-center justify-center animate-fade-in">
                            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-2">C'est l'heure de réviser !</h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm max-w-md mb-6">Tu as des révisions en attente. Choisis par où commencer :</p>
                            <div className="flex flex-wrap justify-center gap-4">
                                {subjectsWithRevisions.map(subject => (
                                    <button
                                        key={subject}
                                        onClick={() => dispatch({ type: 'SELECT_SUBJECT', subject })}
                                        className="flex items-center gap-3 px-5 py-3 rounded-xl font-semibold transition-all duration-300 bg-white/80 dark:bg-black/20 border border-gray-200 dark:border-white/[0.1] hover:bg-cyan-500/10 hover:border-cyan-500/30 hover:text-cyan-700 dark:hover:text-cyan-300 text-gray-700 dark:text-gray-200"
                                    >
                                        <SubjectIcon subject={subject} className="w-5 h-5" />
                                        <span>{subject}</span>
                                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-red-500/10 text-red-600 dark:text-red-400">
                                            {(subjectCounts.cards[subject] || 0) + (subjectCounts.quizzes[subject] || 0)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
                                {currentCardsCount > 0 && (
                                    <div className="group bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-3xl p-8 border transition-all duration-300 relative overflow-hidden flex flex-col justify-between hover:border-fuchsia-500/30 cursor-pointer hover:-translate-y-1 border-gray-200 dark:border-white/[0.05]">
                                        <div className="absolute -top-24 -right-24 w-48 h-48 bg-fuchsia-500/20 blur-[80px] rounded-full pointer-events-none group-hover:bg-fuchsia-500/40 transition-colors duration-500"></div>
                                        <div className="relative z-10 flex-1">
                                            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-3">Flashcards</h3>
                                            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6">Révise tes concepts clés et définitions pour les connaître sur le bout des doigts.</p>
                                        </div>
                                        <button onClick={() => startSession()} disabled={isStartingCards} className="w-full mt-2 px-6 py-3 rounded-xl font-bold transition-all duration-300 relative z-10 text-fuchsia-700 dark:text-white bg-fuchsia-500/10 border border-fuchsia-500/50 group-hover:bg-fuchsia-500/20 group-hover:shadow-[0_0_30px_rgba(217,70,239,0.3)]">
                                            {isStartingCards ? 'Chargement...' : 'Lance la session de révisions'}
                                        </button>
                                    </div>
                                )}
                                {currentQuizzesCount > 0 && (
                                    <div className="group bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-3xl p-8 border transition-all duration-300 relative overflow-hidden flex flex-col justify-between hover:border-emerald-500/30 cursor-pointer hover:-translate-y-1 border-gray-200 dark:border-white/[0.05]">
                                        <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-emerald-500/20 blur-[80px] rounded-full pointer-events-none group-hover:bg-emerald-500/40 transition-colors duration-500"></div>
                                        <div className="relative z-10 flex-1">
                                            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-3">Quiz d'Évaluation</h3>
                                            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6">Teste ta compréhension sur des questions de réflexion.</p>
                                        </div>
                                        <button onClick={() => startQuizSession()} disabled={isStartingQuizzes} className="w-full mt-2 px-6 py-3 rounded-xl font-bold transition-all duration-300 relative z-10 text-emerald-700 dark:text-white bg-emerald-500/10 border border-emerald-500/50 group-hover:bg-emerald-500/20 group-hover:shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                                            {isStartingQuizzes ? 'Chargement...' : 'Vérifie tes connaissances'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </section>

                {/* --- SÉANCES DU JOUR --- */}
                <section>
                    <div className="flex items-center justify-between mb-6 pt-4">
                        <div className="flex items-start">
                            <span className={`p-2 rounded-xl mr-3 shadow-lg flex items-center justify-center transition-colors duration-500 ${areAllEventsCompleted ? 'bg-emerald-500 border border-emerald-500 text-white' : 'bg-white/[0.05] border border-gray-200 dark:border-white/[0.05] text-purple-600 dark:text-purple-400'}`}>
                                {areAllEventsCompleted ? (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                ) : (
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                )}
                            </span>
                            <div className="flex flex-col">
                                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Séances du jour</h2>
                                {selectedSubject !== 'Toutes' && nextEvent && (
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 font-medium">
                                        Prochaine séance : {nextEvent.instance_start.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })} à {nextEvent.instance_start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                )}
                            </div>
                        </div>
                        <button onClick={() => navigate('/agenda')} className="text-sm font-bold text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 transition-colors mt-1">
                            Voir l'agenda
                        </button>
                    </div>
                    {filteredEvents.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in mb-8">
                            {filteredEvents.map((evt, idx) => {
                                const isPast = now >= evt.instance_end;
                                const startStr = evt.instance_start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                                const endStr   = evt.instance_end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                                return (
                                    <div key={`${evt.id}-${idx}`} onClick={() => navigate('/agenda')} className={`group bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-3xl p-6 border border-gray-200 dark:border-white/[0.05] hover:border-purple-500/30 cursor-pointer transition-all duration-300 relative overflow-hidden flex flex-col justify-between ${isPast ? 'opacity-50 grayscale' : ''}`}>
                                        <div className="absolute -top-12 -right-12 w-32 h-32 bg-purple-500/10 blur-[50px] rounded-full pointer-events-none group-hover:bg-purple-500/20 transition-colors duration-500"></div>
                                        <div className="relative z-10 mb-2">
                                            {selectedSubject !== (evt.subject_name || 'Général') && (
                                                <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest mb-2 flex items-center gap-1.5"><SubjectIcon subject={evt.subject_name || 'Général'} className="w-4 h-4" />{evt.subject_name || 'Général'}</span>
                                            )}
                                            <h3 className={`text-lg font-bold ${isPast ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-800 dark:text-white'} leading-tight mb-3`}>{evt.title}</h3>
                                            <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2 font-medium bg-purple-500/10 w-fit px-3 py-1.5 rounded-lg border border-purple-500/20"><span className="text-purple-500">🕒</span> {startStr} - {endStr}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* --- CHAPITRES --- */}
                {displayedChapters.length > 0 && (
                    <section>
                        <div className="flex items-center justify-between mb-6 pt-4">
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center">
                                <span className="p-2 rounded-xl mr-3 shadow-lg flex items-center justify-center bg-white/[0.05] border border-gray-200 dark:border-white/[0.05] text-blue-600 dark:text-blue-400">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
                                </span>
                                <span className="text-gray-800 dark:text-white">Chapitres</span>
                            </h2>
                            {displayedChapters.length > 3 && (
                                <button onClick={() => dispatch({ type: 'TOGGLE_SHOW_ALL_CHAPTERS' })} className="text-sm font-bold text-cyan-600 dark:text-cyan-400 hover:text-cyan-800 dark:hover:text-cyan-300 transition-colors">
                                    {showAllChapters ? 'Voir moins' : 'Voir tout'}
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in">
                            {visibleChapters.map(chapter => {
                                const mastery = chapter.mastery || {};
                                const overall = mastery.overall ?? 0;
                                const levelLabel = chapter.current_level_label ?? null;
                                const lastActivity = mastery.last_activity_at;
                                const daysSince = lastActivity
                                    ? Math.floor((Date.now() - new Date(lastActivity).getTime()) / 86400000)
                                    : null;
                                const freshnessColor = daysSince === null ? 'bg-gray-300 dark:bg-gray-600'
                                    : daysSince <= 7  ? 'bg-emerald-400'
                                    : daysSince <= 30 ? 'bg-amber-400'
                                    : 'bg-red-400';
                                const barColor = overall >= 80 ? 'from-emerald-500 to-cyan-500'
                                    : overall >= 40 ? 'from-blue-500 to-cyan-500'
                                    : 'from-blue-400 to-indigo-500';
                                return (
                                <div key={chapter.id} className="group bg-white/70 dark:bg-white/[0.02] backdrop-blur-xl rounded-3xl p-6 border border-gray-200 dark:border-white/[0.05] hover:border-blue-500/30 transition-all duration-300 relative overflow-hidden flex flex-col justify-between">
                                    <div className="absolute -top-12 -right-12 w-32 h-32 bg-blue-500/10 blur-[50px] rounded-full pointer-events-none group-hover:bg-blue-500/20 transition-colors duration-500"></div>
                                    <div className="relative z-10 mb-4">
                                        {selectedSubject !== (chapter.subject_name || 'Général') && (
                                            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest mb-1 flex items-center gap-1.5">
                                                <SubjectIcon subject={chapter.subject_name || 'Général'} className="w-4 h-4" />
                                                {chapter.subject_name || 'Général'}
                                            </span>
                                        )}
                                        <h3 className="text-lg font-bold text-gray-800 dark:text-white leading-tight">{chapter.name}</h3>
                                    </div>
                                    <div className="relative z-10">
                                        <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 font-semibold mb-1.5">
                                            <span className="flex items-center gap-1.5">
                                                Maîtrise
                                                {daysSince !== null && (
                                                    <span title={daysSince <= 7 ? 'Récent' : daysSince <= 30 ? 'À réviser bientôt' : 'Non pratiqué depuis longtemps'}
                                                        className={`w-2 h-2 rounded-full inline-block ${freshnessColor}`} />
                                                )}
                                                {levelLabel && (
                                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                                                        {levelLabel}
                                                    </span>
                                                )}
                                            </span>
                                            <span className={overall >= 80 ? 'text-emerald-600 dark:text-emerald-400' : ''}>{overall}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 dark:bg-white/[0.05] rounded-full h-1.5 overflow-hidden mb-4"
                                            title={overall > 0 ? `Notions : ${mastery.notion ?? 0}% · Exercices : ${mastery.exercice ?? 0}%` : undefined}>
                                            <div className={`bg-gradient-to-r ${barColor} h-1.5 rounded-full transition-all duration-700`} style={{ width: `${overall}%` }} />
                                        </div>
                                        <button
                                            onClick={() => navigate(`/apprendre/${chapter.subject_slug || generateSlug(chapter.subject_name || 'Général')}/${chapter.slug || generateSlug(chapter.name)}`)}
                                            className="w-full py-3 rounded-xl font-bold text-blue-700 dark:text-white bg-blue-500/10 border border-blue-500/50 group-hover:bg-blue-500/20 group-hover:shadow-[0_0_20px_rgba(59,130,246,0.3)] transition-all duration-300 text-sm"
                                        >
                                            Voir le cours
                                        </button>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                </div>
            )}
            <AICoachChat user={user} onStartFlashcardSession={startSession} onStartQuizSession={startQuizSession} sessionReport={sessionReport} cardsCount={cardsCount} quizzesCount={quizzesCount} pageContext={pageContext} isCoursePage={isCourseViewActive} />
        </>
    );
}

export default EtudiantView;
